/**
 * CRF failures, turned into sentences an operator can act on.
 *
 * Re-authored per feature rather than shared (the quotes/work precedent): this
 * module's 422s are about MODULES, ADVANCES and REMARKS, and a shared helper
 * would grow a branch per feature until nobody could tell which sentence any
 * surface produces.
 *
 * Rules both siblings keep: a 403 never names the permission key (a key is a
 * word for administrators; the reader needs to know who to ask), and 409/422
 * are PASSED THROUGH — the API's own wording names the state or rule it found,
 * and replacing it would lose the one detail that says what to do next.
 */

import { ApiRequestError, ApiTransportError } from '../../lib/api';

export function crfErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') {
      return 'That CRF belongs to another country.';
    }
    if (error.status === 404) return 'That CRF does not exist.';
    if (error.status === 403) {
      return 'This account is not allowed to do that in this country.';
    }
    if (error.status === 409 || error.status === 422 || error.status === 400) {
      return error.message;
    }
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}

/**
 * The remark rule's machine half: `details.reason === 'REMARK_REQUIRED'` carries
 * the OFFENDING point numbers so the grid can badge exactly those rows instead
 * of shrugging at the whole form. Returns [] for every other failure — a caller
 * that badges nothing on an unrelated error is behaving, not broken.
 */
export function remarkRequiredPoints(error: unknown): number[] {
  if (!(error instanceof ApiRequestError)) return [];
  const details = error.details as { reason?: string; points?: number[] } | undefined;
  if (details?.reason !== 'REMARK_REQUIRED') return [];
  return Array.isArray(details.points) ? details.points : [];
}
